import { useState, useEffect } from "react";
import { useSignal } from "@preact/signals-react";
import { endOfDay, startOfDay, parse, subDays } from "date-fns";

import BasicTable from "componentsv2/table/BasicTable";
import Button from "componentsv2/button/Button";
import DatePicker from "componentsv2/form/DatePicker";
import Dropdown from "componentsv2/form/Dropdown";
import Text from "componentsv2/form/Text";
import { layout as layoutApprove, getDeepDomain } from "./ModelApproveGroup";
import { getSections } from "./common";
import { getAccessEditLimit } from "./accessLimit";
import { useTable } from "storev2/tableStore";
import proxy from "api/proxy";
import {
  MessageContainer,
  useMessage,
} from "componentsv2/experimental/Message";

const model = "staff.access";
const layout = layoutApprove();

function ApproveGroupAccess({ onClose, openModal }) {
  const { messages, warning, hide, success, clear, error } = useMessage();
  const [records, setRecords] = useState([]);
  const { getTable, reload, presetTable, resetSelected } = useTable.getState();
  const storeId = "staff.access.approve_group";
  const startDate = useSignal();
  const endDate = useSignal();
  const notes = useSignal("");
  const message = useSignal("");
  const messageType = useSignal("");
  const selectedSection = useSignal(null);

  const fields = {
    start_date: startDate,
    end_date: endDate,
    notes,
    section: selectedSection,
  };

  useEffect(() => {
    presetTable(storeId, layout, null);
    if (openModal && !openModal.value) {
      handleClose();
    }
  }, [openModal?.value]);

  function onChange(fieldName, value) {
    if (fields[fieldName]) fields[fieldName].value = value;
  }

  // function setMessage(text, type, delay = 3000) {
  //   message.value = text;
  //   messageType.value = type;
  //   setTimeout(() => {
  //     message.value = "";
  //     messageType.value = "";
  //   }, delay);
  // }

  function handleClose() {
    startDate.value = "";
    endDate.value = "";
    notes.value = "";
    message.value = "";
    messageType.value = "";
    setRecords([]);
    resetSelected(storeId);
  }

  const attrsStartDate = {
    label: "staff.access.start_date",
    name: "start_date",
    onChange,
  };
  const attrsEndDate = {
    label: "staff.access.end_date",
    name: "end_date",
    onChange,
  };
  const attrsSection = {
    label: "staff.shift_scheduling.section",
    name: "section",
    type: "many2one",
    model: "staff.section",
    onChange: onChange,
    searchable: true,
    preloaded: true,
    preOptions: getSections,
  };

  const attrsNotes = {
    label: "staff.access.notes",
    name: "notes",
    onChange: onChange,
    style: "w-full",
    field: { required: { value: true } },
  };

  const attrsTable = {
    name: "access_records",
    layout: layout,
    selectable: "multi",
  };

  async function handleSearch() {
    if (!startDate.value || !endDate.value) return;

    resetSelected(storeId);

    const _start = startOfDay(parse(startDate.value, "yyyy-MM-dd", new Date()));
    const editLimit = await getAccessEditLimit();
    const limitDateOnly = subDays(startOfDay(new Date()), editLimit);

    if (_start < limitDateOnly) {
      warning(
        editLimit > 0
          ? `No se pueden buscar ni aprobar horas extras con más de ${editLimit} día(s) de antigüedad.`
          : "Solo se pueden buscar y aprobar horas extras del día actual.",
      );
      return;
    }

    let deepDomain = await getDeepDomain();
    if (selectedSection.value) {
      deepDomain = [["section", "=", selectedSection.value.id]];
    }

    const domainOvertime = [
      "OR",
      ["heno", ">", 0],
      ["hedo", ">", 0],
      ["hedf", ">", 0],
      ["henf", ">", 0],
    ];

    const _end = endOfDay(parse(endDate.value, "yyyy-MM-dd", new Date()));

    const domain = [
      ...deepDomain,
      ["enter_timestamp", ">=", _start],
      ["enter_timestamp", "<=", _end],
      ["state", "in", ["close"]],
      domainOvertime,
    ];
    const _domain = { search: domain };

    await reload(storeId, _domain);
    const table = getTable(storeId);
    setRecords(table?.records || []);

    // const tableState = getTable(storeId);
    // setRecords(tableState?.records || []);
  }

  async function handleAction(action) {
    const tableState = getTable(storeId);
    if (!tableState?.selected) return;

    const selected = tableState.selected;
    const ids = Array.from(selected.values()).map((rec) => rec.id);

    if (ids.length === 0) return;

    if (!notes.value) {
      // setMessage("El campo notas es requerido", "error");
      warning("El campo notas es requerido");
      return;
    }

    const response = await proxy.buttonMethod({
      model,
      method: action,
      ids,
      ctx: { notes: notes.value },
    });

    if (response.error) {
      // setMessage("Error al procesar aprobaciones", "error");
      error("Error al procesar aprobaciones");

      return;
    }

    success("Proceso de aprobaciones realizado con éxito!");
    notes.value = "";
    resetSelected(storeId);
    handleSearch();
  }

  return (
    <div className="h-[60vh] flex flex-col relative w-full">
      <div className="flex-none mb-3 grid grid-cols-2 md:grid-cols-4 gap-3 w-full">
        <DatePicker {...attrsStartDate} data={startDate} />
        <DatePicker {...attrsEndDate} data={endDate} />
        <div className="col-span-2 md:col-span-1">
          <Dropdown {...attrsSection} data={selectedSection} />
        </div>
        <div className="col-span-2 md:col-span-1 flex items-end">
          <Button
            color="green"
            content="staff.access.approve_group_filter"
            style="h-10 w-full"
            onClick={handleSearch}
          />
        </div>
      </div>
      <div className="flex-1 min-h-0 flex flex-col relative w-full">
        <div className="flex-1 overflow-auto w-full mb-4">
          <BasicTable {...attrsTable} storeId={storeId} records={records} />
        </div>
        <div className="flex-none w-full flex flex-col gap-4 mt-auto">
          <Text {...attrsNotes} data={notes} />
          <div className="flex max-md:flex-col justify-end gap-3 pt-2 items-center">
            {message.value && (
              <span
                className={`font-medium mr-auto pl-2 flex-1 ${messageType.value === "error" ? "text-rose-600" : "text-lime-600"}`}
              >
                {message.value}
              </span>
            )}
            <MessageContainer
              messages={messages}
              onMessageClose={hide}
              position="top-center"
            />
            <div className="flex gap-3 w-full md:w-auto">
              <Button
                color="blue"
                content="staff.access.state.approved"
                onClick={() => handleAction("approve")}
                style="flex-1 md:flex-none md:w-32"
              />
              <Button
                color="rose"
                content="staff.access.state.not_approved"
                onClick={() => handleAction("not_approve")}
                style="flex-1 md:flex-none md:w-32"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ApproveGroupAccess;
